import betslip, { Betslip, BetslipDefault } from '@gin-betting/betslip';
import core, { App, CoreDefault, GQL } from '@gin-betting/core';
import { REHYDRATE } from '@gin-betting/persist';
import { keys } from 'lodash';
import { TypedEpic } from 'redux-observable';
import getBettingAPI from 'root/getBettingAPI';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/delay';
import 'rxjs/add/observable/empty';

import {
    BetslipAttrManager,
    Destination,
    EventType,
    SidebarView,
} from 'app-constants';
import ui from 'modules/ui';
import {
    Action,
    Category,
    FieldsObj,
    formatPathname,
    formatSearch,
} from 'services/analytics';
import { sendAnalytics } from 'services/analytics/utils';
import UserDevice from 'services/user-device';
import {
    BetslipTimeManager,
    DELETE_BETSLIP_ODD_DELAY as DELETE_BETSLIP_ODD_DELAY_DESK,
    DELETE_BETSLIP_ODD_DELAY_MOB,
    PART_OF_NOT_ENOUGH_DOCS_ERROR,
} from './constants';
import { getAnalyticOddsInfo } from './utils';

const isMobile = UserDevice.getInstance()?.isDeviceMobile();

const deleteBetslipOddDelay = isMobile
    ? DELETE_BETSLIP_ODD_DELAY_MOB
    : DELETE_BETSLIP_ODD_DELAY_DESK;

const {
    async: {
        constants: { UPDATE_BET },
    },
    utils: {
        getIdsFromBetslipOddId,
        getIdsFromBetslipOddPath,
        convertBetStatus,
        getIdsFromOddId,
        getBettingOptions,
    },
} = core;

const {
    constants: {
        DELETE_BETSLIP_BET,
        NAME,
        ADD_BETSLIP_ODD,
        CREATE_BETS_REQUEST,
        CREATE_BETS_SUCCESS,
        CREATE_BETS_FAIL,
        DELETE_BETSLIP_ODD,
    },
    utils: { getBetslipOddId },
    actions: { deleteBetslipBet, deleteBetslipOdd, getMissingMatches },
    selectors: {
        selectBetslipOdds,
        selectBetslipBet,
        selectChosenBetslipBetId,
        selectBetslipOddIdsWithoutEntities,
    },
} = betslip;

const {
    actions: { changeSidebarView, closeSidebar },
    selectors: { selectSidebarView },
} = ui;

type BetslipActionCreators = BetslipDefault['actions'];
type UpdateBetAction = ReturnType<CoreDefault['async']['actions']['updateBet']>;

type DeleteBetslipOddAction = ReturnType<
    BetslipActionCreators['deleteBetslipOdd']
>;
type DeleteBetslipBetAction = ReturnType<
    BetslipActionCreators['deleteBetslipBet']
>;

type AddBetslipOddAction = ReturnType<BetslipActionCreators['addBetslipOdd']>;
type CreateBetRequestAction = ReturnType<BetslipActionCreators['createBet']>;
type CreateBetFailAction = Betslip.CreateBetFailAction;
type CreateBetSuccessAction = Betslip.CreateBetSuccessAction;

type UpdateBetFirstOutputAction =
    | DeleteBetslipOddAction
    | DeleteBetslipBetAction;

type UpdateBetOutputAction =
    | UpdateBetFirstOutputAction
    | ReturnType<typeof changeSidebarView>
    | ReturnType<typeof closeSidebar>;

export const updateBetEpic$: TypedEpic<
    UpdateBetAction,
    UpdateBetOutputAction
> = (actions$, store) =>
    actions$
        .ofType(UPDATE_BET)
        .delay(deleteBetslipOddDelay)
        .mergeMap((action): UpdateBetFirstOutputAction[] => {
            const {
                entities: { bets },
            } = action.payload;
            const incomeBetId = Object.keys(bets)[0];

            const appState = store.getState();

            const betslipOdds = selectBetslipOdds(appState);

            // all betslipOddIds belong to one betslipBet
            const betslipOddIdsToDelete = betslipOdds
                .filter(
                    ({ betId, singleStatus }) =>
                        betId === incomeBetId &&
                        singleStatus === App.BetslipStatus.SUCCESS
                )
                .keySeq()
                .toSet();

            const betslipOddId = betslipOddIdsToDelete.first<
                string | undefined
            >();

            if (!betslipOddId) {
                console.warn('betslipOdd is not exist', incomeBetId);

                return [];
            }

            const { betslipBetId } = getIdsFromBetslipOddId(betslipOddId);

            const betslipBet = selectBetslipBet(appState, {
                betslipBetId,
            });

            if (!betslipBet) {
                console.warn('betslipBet is not exist', incomeBetId);

                return [];
            }

            const { betslipOddIds } = betslipBet;

            if (betslipOddIds.toSet().equals(betslipOddIdsToDelete)) {
                return [deleteBetslipBet({ betslipBetId })];
            }

            return betslipOddIdsToDelete.toArray().map(id => {
                const { betslipBetId: bBetId, oddId } = getIdsFromBetslipOddId(
                    id
                );

                return deleteBetslipOdd({
                    betslipBetId: bBetId,
                    oddId,
                });
            });
        })
        .mergeMap(
            (action: UpdateBetFirstOutputAction): UpdateBetOutputAction[] => {
                const { betslipBetId } = action.payload;

                const appState = store.getState();

                const chosenBetslipBetId = selectChosenBetslipBetId(appState);
                const sidebarView = selectSidebarView(appState);

                const result: UpdateBetOutputAction[] = [action];

                if (
                    action.type === DELETE_BETSLIP_BET &&
                    chosenBetslipBetId === betslipBetId &&
                    sidebarView === SidebarView.betslip
                ) {
                    result.push(
                        isMobile
                            ? closeSidebar()
                            : changeSidebarView({
                                  sidebarView: SidebarView.bets,
                              })
                    );
                }

                return result;
            }
        );

export const betConfirmEpic$: TypedEpic<UpdateBetAction, never> = actions$ =>
    actions$.ofType(UPDATE_BET).mergeMap(action => {
        const {
            entities: { bets },
        } = action.payload;

        const betId = keys(bets)[0];

        if (!betId) return Observable.empty();

        const bet = bets[betId];

        if (!bet) return Observable.empty();

        const { stake, currencyCode, status, declineReason } = bet;

        if (status === GQL.BetStatus.ACCEPTED) {
            getBettingAPI().bettingEvents$.dispatch(EventType.BetConfirm, {
                stake,
                currency: currencyCode,
            });
        }

        if (declineReason.includes(PART_OF_NOT_ENOUGH_DOCS_ERROR)) {
            getBettingAPI().bettingEvents$.dispatch(
                EventType.HandleNotEnoughDocs,
                {}
            );
        }

        return Observable.empty();
    });

export const addOddEpic$: TypedEpic<
    Betslip.Actions['addBetslipOdd'],
    never
> = actions$ =>
    actions$.ofType(ADD_BETSLIP_ODD).mergeMap(() => {
        getBettingAPI().bettingEvents$.dispatch(EventType.AddOdd, {});

        const { isAuthorized } = getBettingOptions();

        if (!isAuthorized) {
            getBettingAPI().bettingEvents$.dispatch(EventType.Redirect, {
                destination: Destination.Login,
                link: '',
            });
        }

        return Observable.empty();
    });

export const getMissingMatches$: TypedEpic = (action$, store) => {
    return action$
        .ofType(REHYDRATE)
        .filter(({ key }) => key === NAME)
        .switchMap(() => {
            const betslipOddIds = selectBetslipOddIdsWithoutEntities(
                store.getState()
            );

            const variables = betslipOddIds.reduce(
                (
                    acc: {
                        marketIds: Set<string>;
                        sportEventsIds: Set<string>;
                    },
                    betslipOddId
                ) => {
                    const { marketId, matchId } = getIdsFromBetslipOddPath(
                        betslipOddId
                    );

                    acc.marketIds.add(marketId);
                    acc.sportEventsIds.add(matchId);

                    return acc;
                },
                {
                    marketIds: new Set<string>(),
                    sportEventsIds: new Set<string>(),
                }
            );

            const { sportEventsIds, marketIds } = variables;

            if (sportEventsIds.size === 0 || marketIds.size === 0) {
                return Observable.empty();
            }

            return Observable.of(
                getMissingMatches({
                    marketIds: Array.from(marketIds),
                    sportEventsIds: Array.from(sportEventsIds),
                })
            );
        });
};

export const toggleBetslipOdd: TypedEpic<
    Betslip.Actions['addBetslipOdd'],
    Betslip.Actions['deleteBetslipOdd']
> = (action$, state$) =>
    action$
        .ofType(ADD_BETSLIP_ODD)
        .mergeMap(({ payload: { oddId: incomeOddId, betslipBetId } }) => {
            const ids = getIdsFromOddId(incomeOddId);
            const state = state$.getState();
            const betslipBet = selectBetslipBet(state, { betslipBetId });

            if (!betslipBet || !ids) return Observable.empty();

            const { betslipOddIds } = betslipBet;

            const betslipOddIdsToDelete = betslipOddIds.reduce(
                (acc: string[], betslipOddId) => {
                    const { matchId, oddId } = getIdsFromBetslipOddId(
                        betslipOddId
                    );

                    if (incomeOddId !== oddId && ids.matchId === matchId) {
                        acc.push(oddId);
                    }

                    return acc;
                },
                []
            );

            return betslipOddIdsToDelete.map(oddId =>
                deleteBetslipOdd({ betslipBetId, oddId })
            );
        });

export const updateManagerOnBetslipOddAdd$: TypedEpic<
    AddBetslipOddAction,
    never
> = actions$ =>
    actions$.ofType(ADD_BETSLIP_ODD).mergeMap(action => {
        const { oddId, betslipBetId } = action.payload;
        const betslipOddId = getBetslipOddId({ oddId, betslipBetId });

        BetslipTimeManager[betslipOddId] = Date.now();

        return Observable.empty();
    });

export const clearManagerOnBetslipOddDelete$: TypedEpic<
    DeleteBetslipOddAction,
    never
> = actions$ =>
    actions$.ofType(DELETE_BETSLIP_ODD).mergeMap(action => {
        const { oddId, betslipBetId } = action.payload;
        const betslipOddId = getBetslipOddId({ oddId, betslipBetId });

        delete BetslipTimeManager[betslipOddId];

        return Observable.empty();
    });

export const sendAnalyticsOnPlaceBet$: TypedEpic<
    CreateBetRequestAction | CreateBetFailAction,
    never
> = actions$ =>
    actions$.ofType(CREATE_BETS_REQUEST, CREATE_BETS_FAIL).mergeMap(action => {
        const {
            type,
            payload: {
                args: { betslipOddIds },
            },
        } = action;

        const eventLabel = getAnalyticOddsInfo({ betslipOddIds });

        if (!eventLabel) return Observable.empty();

        const eventAction =
            type === CREATE_BETS_REQUEST ? Action.PlaceBet : Action.DeclineBet;

        const fieldsObject: FieldsObj = {
            eventCategory: Category.Betslip,
            eventAction,
            eventLabel,
        };

        sendAnalytics(fieldsObject, eventLabel);

        return Observable.empty();
    });

export const sendAnalyticsOnBetResult$: TypedEpic<
    CreateBetSuccessAction | UpdateBetAction,
    never
> = (actions$, store) =>
    actions$.ofType(UPDATE_BET, CREATE_BETS_SUCCESS).switchMap(action => {
        const {
            entities: { bets },
        } = action.payload;

        const betId = keys(bets)[0];

        if (!betId) return Observable.empty();

        const bet = bets[betId];

        if (!bet) return Observable.empty();

        const { status } = bet;

        if (convertBetStatus(status) === App.BetslipStatus.PROCESSING) {
            return Observable.empty();
        }

        const betslipOdds = selectBetslipOdds(store.getState());

        const currentEventAction =
            convertBetStatus(status) === App.BetslipStatus.SUCCESS
                ? Action.AcceptedBet
                : Action.DeclineBet;

        const betslipOddIds = betslipOdds
            .filter(betslipOdd => betslipOdd.betId === betId)
            .keySeq()
            .toOrderedSet();

        const { search, pathname, hash } = window.location;

        const placeUrl = `${formatPathname(pathname)}${formatSearch(
            search
        )}${hash}`;

        betslipOddIds.forEach(betslipOddId => {
            const { category, label } = BetslipAttrManager[betslipOddId] || {};

            if (category && label) {
                const eventLabel = JSON.stringify({
                    ...label,
                    placeUrl,
                });
                const fieldsObject: FieldsObj = {
                    eventCategory: category,
                    eventAction: Action.PlaceOdd,
                };

                sendAnalytics(fieldsObject, eventLabel);
            }

            delete BetslipAttrManager[betslipOddId];
        });

        const eventLabel = getAnalyticOddsInfo({
            betslipOddIds,
            withClear: true,
            betId,
        });

        if (!eventLabel) return Observable.empty();

        const fieldsObject: FieldsObj = {
            eventCategory: Category.Betslip,
            eventAction: currentEventAction,
        };

        sendAnalytics(fieldsObject, eventLabel);

        return Observable.empty();
    });
